import React, { useState, useEffect } from "react"; 
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import AcademicDetails from "./AcademicDetails";
import ExperienceDetails from "./ExperienceDetails";
import DocumentRecords from "./DocumentRecords";
import PersonalDetails from "./PersonalDetails";
import ProfileSection from "./ProfileSection";
import LeaveManagement from "./LeaveManagement";
import AttendanceManagement from "./AttendanceManagement";
import Timesheets from "./Timesheets";
import EmployeeProjects from "./EmployeeProjects";
import EmployeeMeetings from "./EmployeeMeetings";
import EmployeePerformance from "./EmployeePerformance";
import HiringRequirements from "./HiringRequirements";
import API_BASE_URL from "../config";
import TeamChatModal from "./TeamChatModal";
import ChatsTab from "./ChatsTab";
import { toast } from "react-toastify";

//css file 
import "./EmployeeDashboard.css";

// Import React Icons
import {
  MdPerson,
  MdSchool,
  MdWork,
  MdDescription,
  MdEventNote,
  MdLocationOn,
  MdAccessTime,
  MdAssessment,
  MdGroup,
  MdSupervisorAccount,
  MdFolder,
  MdChat,
  MdBuild,
  MdInventory,
  MdAssignment,
  MdEdit,
  MdRefresh,
  MdArrowBack,
  MdExitToApp,
  MdEmail,
  MdBusiness,
  MdPhone,
  MdWarning,
  MdCheckCircle,
  MdCalendarToday,
  MdBusinessCenter,
  MdApartment,
  MdMoreVert,
  MdMenu,
  MdClose
} from "react-icons/md";

const EmployeeDashboard = () => {
  const [employee, setEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState("Personal Details");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [myTeam, setMyTeam] = useState([]);
  const [reportingManager, setReportingManager] = useState(null);
  const [allocatedItems, setAllocatedItems] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isHRView, setIsHRView] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const [personalInfo, setPersonalInfo] = useState(null);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [managerLoading, setManagerLoading] = useState(false);

  const [loadingPersonalInfo, setLoadingPersonalInfo] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedChatManager, setSelectedChatManager] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newChatMessage, setNewChatMessage] = useState('');

  const [profile, setProfile] = useState(null);

  const [managerChatOpen, setManagerChatOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [managerMessages, setManagerMessages] = useState([]);
  const [managerMessageInput, setManagerMessageInput] = useState('');

  const navigateToManagerChat = (manager) => {
    setChatModalOpen(true);
    setSelectedChatManager(manager);
    openChatWithManager(manager);
  };

  const openManagerChat = (manager) => {
    console.log('Opening manager chat with:', manager);
    
    const managerData = {
      _id: manager._id,
      name: manager.name || 'Reporting Manager',
      department: manager.department,
      email: manager.email,
      mobile: manager.mobile,
      company_name: manager.company_name || employee.company_name,
      profileImage: manager.profileImage || manager.profilePicture,
      isOnline: true
    };
    
    setSelectedManager(managerData);
    setManagerChatOpen(true);
    setManagerMessages([]);
    setManagerMessageInput('');
    
    loadManagerMessages(managerData._id);
  };

  const loadManagerMessages = async (managerId) => {
    try {
      if (!employee?._id || !managerId) return;
      
      console.log('Loading manager messages...');
      
      const endpoints = [
        `${API_BASE_URL}/api/chat/manager/${managerId}/employee/${employee._id}`,
        `${API_BASE_URL}/api/manager-chat/conversation?employeeId=${employee._id}&managerId=${managerId}`,
        `${API_BASE_URL}/api/messages?employeeId=${employee._id}&managerId=${managerId}&type=manager`
      ];
      
      for (const endpoint of endpoints) {
        try {
          const response = await axios.get(endpoint);
          
          if (response.data && (response.data.messages || response.data.data)) {
            const messages = response.data.messages || response.data.data || [];
            console.log('Manager messages loaded:', messages.length);
            setManagerMessages(messages);
            return;
          }
        } catch (err) {
          console.log(`Endpoint ${endpoint} failed:`, err.message);
        }
      }
      
      console.log('No existing manager chat found');
      setManagerMessages([]);
      
    } catch (error) {
      console.error('Error loading manager messages:', error);
      setManagerMessages([]);
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }

    try {
      const formData = new FormData();
      formData.append('profilePicture', file);
      formData.append('employeeId', employee._id);
      formData.append('emp_id', employee.emp_id);
      formData.append('name', employee.name);

      const token = sessionStorage.getItem("emp_token");
      
      console.log("📤 Uploading profile picture for employee:", employee._id);
      
      const response = await axios.post(
        `${API_BASE_URL}/api/employees/upload-profile-picture`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': token ? `Bearer ${token}` : ''
          }
        }
      );

      if (response.data.success) {
        toast.success("Profile picture updated successfully!");
        
        const updatedEmployee = {
          ...employee,
          profilePicture: response.data.profilePictureUrl || response.data.data?.profilePicture
        };
        
        setEmployee(updatedEmployee);
        sessionStorage.setItem("employee", JSON.stringify(updatedEmployee));
        window.location.reload();
      } else {
        throw new Error(response.data.message || "Upload failed");
      }
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      toast.error(error.response?.data?.message || "Failed to upload profile picture");
    }
  };

  const sendManagerMessage = async () => {
    if (!managerMessageInput.trim() || !selectedManager?._id || !employee?._id) {
      toast.error('Cannot send empty message');
      return;
    }
    
    const messageText = managerMessageInput.trim();
    const tempId = `temp_${Date.now()}`;
    
    const tempMessage = {
      _id: tempId,
      senderId: employee._id,
      senderName: employee.name,
      receiverId: selectedManager._id,
      receiverName: selectedManager.name,
      message: messageText,
      timestamp: new Date().toISOString(),
      status: 'sending',
      type: 'manager'
    };
    
    setManagerMessages(prev => [...prev, tempMessage]);
    setManagerMessageInput('');
    
    try {
      const sendData = {
        employeeId: employee._id,
        employeeName: employee.name,
        managerId: selectedManager._id,
        managerName: selectedManager.name,
        message: messageText,
        company: employee.company_name,
        timestamp: new Date().toISOString()
      };
      
      const endpoints = [
        `${API_BASE_URL}/api/manager-chat/send`,
        `${API_BASE_URL}/api/chat/manager/send`,
        `${API_BASE_URL}/api/messages/send-to-manager`
      ];
      
      let success = false;
      
      for (const endpoint of endpoints) {
        try {
          const response = await axios.post(endpoint, sendData);
          
          if (response.data?.success) {
            setManagerMessages(prev => 
              prev.map(msg => 
                msg._id === tempId 
                  ? { ...response.data.message, status: 'sent' }
                  : msg
              )
            );
            
            toast.success('Message sent to manager!');
            success = true;
            break;
          }
        } catch (err) {
          console.log(`Send to ${endpoint} failed:`, err.message);
        }
      }
      
      if (!success) {
        setManagerMessages(prev => 
          prev.map(msg => 
            msg._id === tempId 
              ? { ...msg, status: 'failed' }
              : msg
          )
        );
        
        toast.error('Failed to send message. Please try again.');
        setManagerMessageInput(messageText);
      }
      
    } catch (error) {
      console.error('Error sending manager message:', error);
      setManagerMessages(prev => 
        prev.map(msg => 
          msg._id === tempId 
            ? { ...msg, status: 'failed' }
            : msg
        )
      );
      setManagerMessageInput(messageText);
      toast.error('Error sending message');
    }
  };

  const fetchManagerChatMessages = async (managerId) => {
    try {
      if (!employee?._id || !managerId) {
        console.log('Missing employee or manager ID');
        return;
      }

      console.log('Fetching manager chat messages...');
      
      const response = await axios.get(
        `${API_BASE_URL}/api/manager-chat/messages`,
        {
          params: {
            employeeId: employee._id,
            managerId: managerId,
            employeeName: employee.name,
            managerName: selectedChatManager?.name
          }
        }
      );

      if (response.data.success) {
        console.log('Manager messages received:', response.data.messages);
        setChatMessages(response.data.messages || []);
      } else {
        console.log('No existing manager chat');
        setChatMessages([]);
      }
    } catch (error) {
      console.error('Error fetching manager chat:', error);
      setChatMessages([]);
    }
  };

  const sendMessageToManager = async () => {
    if (!newChatMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    if (!selectedChatManager?._id) {
      toast.error('No manager selected');
      return;
    }

    if (!employee?._id) {
      toast.error('No employee ID found');
      return;
    }

    const messageData = {
      senderId: employee._id,
      senderName: employee.name,
      senderType: 'employee',
      receiverId: selectedChatManager._id,
      receiverName: selectedChatManager.name,
      receiverType: 'manager',
      company: employee.company_name,
      message: newChatMessage.trim(),
      timestamp: new Date()
    };

    console.log('Sending to manager:', messageData);

    const tempMessage = {
      _id: `temp_${Date.now()}`,
      ...messageData,
      sentAt: new Date().toISOString(),
      status: 'sending'
    };

    setChatMessages(prev => [...prev, tempMessage]);
    const messageToSend = newChatMessage;
    setNewChatMessage('');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/manager-chat/send`,
        messageData
      );

      if (response.data.success) {
        console.log('Message sent to manager successfully');
        
        setChatMessages(prev =>
          prev.map(msg =>
            msg._id === tempMessage._id
              ? { ...response.data.message, status: 'sent' }
              : msg
          )
        );
        
        toast.success('Message sent to manager!');
      } else {
        throw new Error(response.data.message || 'Failed to send');
      }
    } catch (error) {
      console.error('Error sending to manager:', error);
      
      setChatMessages(prev =>
        prev.map(msg =>
          msg._id === tempMessage._id
            ? { ...msg, status: 'failed' }
            : msg
        )
      );
      
      setNewChatMessage(messageToSend);
      toast.error(`Failed to send: ${error.response?.data?.message || error.message}`);
    }
  };

  const openChatWithManager = (manager) => {
    setSelectedChatManager(manager);
    setChatModalOpen(true);
    setChatMessages([]);
    fetchManagerChatMessages(manager._id);
  };

  const getProfilePicture = (user) => {
    if (!user) {
      console.log("❌ getProfilePicture: User is null");
      return null;
    }
    
    console.log("🖼️ getProfilePicture called for:", user.name || 'Unknown');
    
    const imageFields = [
      'profilePicture',
      'profilePic', 
      'photo',
      'profile_image',
      'avatar',
      'image',
      'profileImage',
      'picture',
      'profile_picture',
      'profilePictureUrl'
    ];
    
    let profileImage = null;
    let foundField = null;
    
    for (const field of imageFields) {
      if (user[field] && typeof user[field] === 'string' && user[field].trim() !== '') {
        profileImage = user[field];
        foundField = field;
        break;
      }
    }
    
    console.log("🔍 Image search results:", {
      userKeys: Object.keys(user),
      foundField: foundField,
      imageValue: profileImage
    });
    
    if (profileImage) {
      let cleanImage = profileImage.trim();
      
      if (cleanImage.startsWith('http://') || cleanImage.startsWith('https://')) {
        console.log("✅ Full URL found:", cleanImage);
        return cleanImage;
      }
      
      if (cleanImage.startsWith('/')) {
        cleanImage = cleanImage.replace(/^\/+/g, '/');
        const fullUrl = `${API_BASE_URL}${cleanImage}`;
        console.log("✅ Built URL from path:", fullUrl);
        return fullUrl;
      }
      
      if (!cleanImage.includes('/')) {
        const fullUrl = `${API_BASE_URL}/uploads/${cleanImage}`;
        console.log("✅ Built URL with uploads path:", fullUrl);
        return fullUrl;
      }
      
      const fullUrl = `${API_BASE_URL}${cleanImage.startsWith('/') ? '' : '/'}${cleanImage}`;
      console.log("✅ Built URL with relative path:", fullUrl);
      return fullUrl;
    }
    
    console.log("❌ No profile image found in any field");
    return null;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(" ").map((n) => n[0]).join("").toUpperCase();
  };

  const handleContactClick = (teamMember) => {
    setSelectedTeamMember(teamMember);
    setChatModalOpen(true);
  };

  const handleCloseChat = () => {
    setChatModalOpen(false);
    setSelectedTeamMember(null);
  };

  const toggleDropdown = (dropdownName) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  const isValidDate = (dateString) => {
    if (!dateString || dateString === "null" || dateString.trim() === "") {
      return false;
    }
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, '');
    
    if (cleaned.length === 10) {
      return `+91 ${cleaned.slice(0,5)} ${cleaned.slice(5)}`;
    }
    
    return phone;
  };

  const refreshPersonalInfo = async () => {
    try {
      setRefreshing(true);
      console.log("🔄 Refreshing personal info...");
      
      if (employee?._id) {
        await fetchPersonalInfo();
        toast.success("Personal information refreshed successfully!");
      }
    } catch (error) {
      console.error("Error refreshing personal info:", error);
      toast.error("Failed to refresh personal information");
    } finally {
      setRefreshing(false);
    }
  };

  const fetchPersonalInfo = async () => {
    try {
      setLoadingPersonalInfo(true);
      
      if (!employee || !employee.emp_id) {
        console.log("❌ Employee data not available yet");
        setPersonalInfo(null);
        setFormSubmitted(false);
        return;
      }
      
      console.log("🔄 Fetching personal info for emp_id:", employee.emp_id);
      
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/personal-info/emp_id/${employee.emp_id}`
        );
        
        console.log("✅ API Response status:", res.status);
        
        if (res.data && res.data.success && res.data.data) {
          console.log("🎯 Valid personal info received:", res.data.data);
          
          const personalData = res.data.data;
          setPersonalInfo(personalData);
          
          const requiredFields = ['fatherName', 'motherName', 'dateOfBirth', 'bloodGroup', 'mobileNumber', 'emergencyContact', 'address'];
          const allFieldsFilled = requiredFields.every(field => {
            const hasValue = personalData[field] && personalData[field].toString().trim() !== '';
            return hasValue;
          });
          
          console.log("📝 All fields filled:", allFieldsFilled);
          setFormSubmitted(allFieldsFilled);
          sessionStorage.setItem('personalInfo', JSON.stringify(personalData));
        } else if (res.data && !res.data.success) {
          console.log("⚠️ API returned success: false", res.data.message);
          setPersonalInfo(null);
          setFormSubmitted(false);
        } else {
          console.log("⚠️ No personal info found");
          setPersonalInfo(null);
          setFormSubmitted(false);
        }
      } catch (apiError) {
        console.error("❌ API Error:", apiError.message);
        
        try {
          console.log("🔄 Trying alternative endpoint by employeeId...");
          const altRes = await axios.get(
            `${API_BASE_URL}/api/personal-info/${employee._id}`
          );
          
          console.log("✅ Alternative API Response:", altRes.data);
          
          if (altRes.data && altRes.data.success && altRes.data.data) {
            const personalData = altRes.data.data;
            setPersonalInfo(personalData);
            
            const requiredFields = ['fatherName', 'motherName', 'dateOfBirth', 'bloodGroup', 'mobileNumber', 'emergencyContact', 'address'];
            const allFieldsFilled = requiredFields.every(field => 
              personalData[field] && personalData[field].toString().trim() !== ''
            );
            
            setFormSubmitted(allFieldsFilled);
            sessionStorage.setItem('personalInfo', JSON.stringify(personalData));
          }
        } catch (altError) {
          console.error("❌ Alternative endpoint also failed:", altError.message);
          setPersonalInfo(null);
          setFormSubmitted(false);
        }
      }
    } catch (err) {
      console.error("❌ Error fetching personal info:", err);
      setPersonalInfo(null);
      setFormSubmitted(false);
    } finally {
      setLoadingPersonalInfo(false);
    }
  };

  useEffect(() => {
    if (employee?._id) {
      console.log("📞 EmployeeDashboard useEffect: Fetching personal info");
      fetchPersonalInfo();
    }
  }, [employee]);

  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        setLoading(true);
        
        if (employeeId) {
          console.log("HR View - Fetching employee data for ID:", employeeId);
          setIsHRView(true);
          
          const empRes = await axios.get(`${API_BASE_URL}/api/employees/${employeeId}`);
          
          if (empRes.data && empRes.data.success) {
            const employeeData = empRes.data.data;
            console.log("Employee data loaded:", employeeData);
            setEmployee(employeeData);
          } else {
            console.error("Employee not found");
            navigate('/dashboard');
          }
        } else {
          console.log("Regular Employee Login");
          setIsHRView(false);
          
          const empData = JSON.parse(sessionStorage.getItem("employee"));
          const empToken = sessionStorage.getItem("emp_token");
          
          if (!empData) {
            console.log("❌ No employee data in sessionStorage, redirecting to login");
            navigate("/login");
            return;
          }

          console.log("✅ Employee data from sessionStorage:", empData);
          
          if (!empData._id) {
            console.error("❌ Employee ID is undefined in sessionStorage data");
            
            try {
              const empRes = await axios.get(
                `${API_BASE_URL}/api/employees?emp_id=${empData.emp_id}`
              );
              
              if (empRes.data && empRes.data.length > 0) {
                const foundEmployee = empRes.data[0];
                console.log("✅ Found employee by emp_id:", foundEmployee);
                setEmployee(foundEmployee);
                sessionStorage.setItem("employee", JSON.stringify(foundEmployee));
              } else {
                navigate("/login");
                return;
              }
            } catch (err) {
              console.error("Error finding employee:", err);
              navigate("/login");
              return;
            }
          } else {
            try {
              console.log("🔄 Fetching updated employee data for ID:", empData._id);
              
              const empRes = await axios.get(
                `${API_BASE_URL}/api/employees/${empData._id}`,
                {
                  headers: {
                    'Authorization': empToken ? `Bearer ${empToken}` : ''
                  }
                }
              );
              
              if (empRes.data && empRes.data.success) {
                const updatedEmployee = empRes.data.data;
                console.log("✅ Updated employee data:", updatedEmployee);
                setEmployee(updatedEmployee);
                sessionStorage.setItem("employee", JSON.stringify(updatedEmployee));
              } else {
                console.log("⚠️ Using stored employee data");
                setEmployee(empData);
              }
            } catch (err) {
              console.error("Error fetching updated employee data:", err);
              console.log("⚠️ Using fallback employee data");
              setEmployee(empData);
            }
          }
        }
      } catch (err) {
        console.error("Error in fetchEmployeeData:", err);
        if (employeeId) {
          navigate('/dashboard');
        } else {
          const empData = JSON.parse(sessionStorage.getItem("employee"));
          if (empData) {
            console.log("⚠️ Using stored employee data after error");
            setEmployee(empData);
          } else {
            navigate("/login");
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, [navigate, employeeId]);

  useEffect(() => {
    if (activeTab === "My Team" && employee) {
      fetchMyTeam();
    }
  }, [activeTab, employee]);

  useEffect(() => {
    if (activeTab === "Reporting Manager" && employee) {
      fetchReportingManager();
    }
  }, [activeTab, employee]);

  useEffect(() => {
    if (activeTab === "Allocated Items" && employee) {
      fetchAllocatedItems();
    }
  }, [activeTab, employee]);

  const fetchMyTeam = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/employees/company/${employee.company_name}`
      );
      const teamMembers = res.data.filter((e) => e._id !== employee._id);
      setMyTeam(teamMembers);
    } catch (err) {
      console.error("Error fetching team:", err);
    }
  };

  const fetchReportingManager = async () => {
    try {
      setManagerLoading(true);
      
      const managerName = employee.reporting_manager;
      
      if (!managerName) {
        setReportingManager(null);
        return;
      }
      
      const token = sessionStorage.getItem("token");
      const companyName = sessionStorage.getItem("companyName") || employee.company_name;
      
      const response = await fetch(
        `${API_BASE_URL}/api/managers?company_name=${encodeURIComponent(companyName)}`,
        {
          headers: { 
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
      
      if (response.ok) {
        const managers = await response.json();
        
        const foundManager = managers.find(mgr => 
          mgr.name && mgr.name.toLowerCase() === managerName.toLowerCase()
        );
        
        if (foundManager) {
          const managerDetailResponse = await fetch(
            `${API_BASE_URL}/api/managers/me?id=${foundManager._id}`,
            {
              headers: { 
                "Authorization": `Bearer ${token}`,
                "Content-Type": "application/json"
              }
            }
          );
          
          if (managerDetailResponse.ok) {
            const managerData = await managerDetailResponse.json();
            setReportingManager({
              ...managerData.manager,
              source: 'manager_db'
            });
          } else {
            setReportingManager({
              ...foundManager,
              source: 'manager_db'
            });
          }
        } else {
          const foundById = managers.find(mgr => 
            mgr.manager_id === managerName || 
            mgr.email === managerName
          );
          
          if (foundById) {
            setReportingManager({
              ...foundById,
              source: 'manager_db'
            });
          } else {
            setReportingManager({
              name: managerName,
              isPlaceholder: true,
              source: 'placeholder',
              role: "Reporting Manager",
              department: employee.department,
              company_name: companyName
            });
          }
        }
      } else {
        setReportingManager({
          name: managerName,
          isPlaceholder: true,
          source: 'placeholder',
          role: "Reporting Manager",
          department: employee.department,
          company_name: companyName
        });
      }
    } catch (error) {
      console.error("Error fetching reporting manager:", error);
      setReportingManager({
        name: employee.reporting_manager,
        isPlaceholder: true,
        source: 'placeholder',
        role: "Reporting Manager",
        department: employee.department,
        company_name: employee.company_name
      });
    } finally {
      setManagerLoading(false);
    }
  };

  const fetchAllocatedItems = async () => {
    try {
      if (employee.allocated_item) {
        const items = employee.allocated_item.split(',').map(item => item.trim());
        setAllocatedItems(items);
      } else {
        const res = await axios.get(
          `${API_BASE_URL}/api/allocate-items?company_name=${employee.company_name}`
        );
        setAllocatedItems(Array.isArray(res.data) ? res.data : []);
      }
    } catch (err) {
      console.error("Error fetching allocated items:", err);
      setAllocatedItems([]);
    }
  };

  const handleBackToHR = () => {
    navigate('/hr-dashboard');
  };

  const handleLogout = () => {
    sessionStorage.removeItem("employee");
    navigate("/login");
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handlePersonalInfoUpdate = (updatedData) => {
    console.log("🔄 EmployeeDashboard: Received updated personal info", updatedData);
    setPersonalInfo(updatedData);
    setFormSubmitted(true);
    setTimeout(() => {
      fetchPersonalInfo();
    }, 500);
  };

  useEffect(() => {
    const handlePersonalInfoEvent = () => {
      console.log("📢 Personal info update event received");
      if (employee?._id) {
        setTimeout(() => {
          fetchPersonalInfo();
        }, 1000);
      }
    };

    window.addEventListener('personalInfoUpdated', handlePersonalInfoEvent);
    
    return () => {
      window.removeEventListener('personalInfoUpdated', handlePersonalInfoEvent);
    };
  }, [employee]);

  const navTabs = [
    { id: "Personal Details", icon: "👤", label: "Personal" },
    { id: "Academic Details", icon: "🎓", label: "Academic" },
    { id: "Experience Details", icon: "💼", label: "Experience" },
    { id: "Document Records", icon: "📄", label: "Documents" },
    { id: "My Team", icon: "👥", label: "My Team" },
    { id: "Reporting Manager", icon: "👨‍💼", label: "Manager" },
    { id: "Projects", icon: "🚀", label: "Projects" },
    { id: "Tools", icon: "🛠️", label: "Tools" },
    { id: "Leave Management", icon: "🏖️", label: "Leave" },
    { id: "Attendance Management", icon: "📊", label: "Attendance" },
    { id: "Timesheet", icon: "⏰", label: "Timesheet" },
    { id: "Employee Performance", icon: "⭐", label: "Performance" },
    { id: "Chats", icon: "💬", label: "Chats" },
    { id: "Requirements", icon: "📋", label: "Requirements" }
  ];

  if (loading) {
    return (
      <div className="ed-loading-container">
        <div className="ed-loading-spinner"></div>
        <p className="ed-loading-text">
          {isHRView ? "Loading employee profile..." : "Loading your dashboard..."}
        </p>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="ed-error-container">
        <div className="ed-error-content">
          <h2>Employee Not Found</h2>
          <p>The employee data could not be loaded.</p>
          {isHRView ? (
            <button className="primary-btn" onClick={handleBackToHR}>
              Back to HR Dashboard
            </button>
          ) : (
            <button className="primary-btn" onClick={() => navigate("/login")}>
              Go to Login
            </button>
          )}
        </div>
      </div>
    );
  }

  const employeeProfilePic = getProfilePicture(employee);

  const hasPersonalInfoData = personalInfo && 
    Object.values(personalInfo).some(
      value => value && 
      typeof value === 'string' && 
      value.trim() !== '' &&
      !['_id', 'employeeId', '__v'].some(field => personalInfo[field] === value)
    );

  return (
    <div className="ed-container">
      {/* Header */}
      <header className="ed-header">
        <div className="ed-header-content">
          <div className="ed-logo-section">
            <button className="ed-sidebar-toggle" onClick={toggleSidebar}>
              <MdMenu size={24} />
            </button>
            
            <div className="ed-logo">
              <span className="ed-logo-text">{employee.company_name} Portal</span>
              {isHRView && (
                <span className="ed-hr-view-badge">HR View Mode</span>
              )}
            </div>
          </div>
          
          <div className="ed-user-section">
            <div className="ed-user-info">
              <span className="ed-user-name">
                {employee.name}
                {isHRView && " (HR View)"}
              </span>
              <span className="ed-user-role">{employee.role}</span>
            </div>
            
            {isHRView ? (
              <button className="ed-back-to-hr" onClick={handleBackToHR}>
                <MdArrowBack /> Back to HR
              </button>
            ) : (
              (() => {
                let profilePicture = null;
                
                if (profile?.profilePic) {
                  profilePicture = profile.profilePic;
                }
                else if (employee?.profilePicture) {
                  profilePicture = `${API_BASE_URL}${employee.profilePicture}`;
                }
                
                return profilePicture ? (
                  <img 
                    src={profilePicture} 
                    alt="Profile" 
                    className="ed-user-profile-pic"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const avatar = e.target.parentElement.querySelector('.ed-user-avatar');
                      if (avatar) avatar.style.display = 'flex';
                    }}
                  />
                ) : (
                  <div className="ed-user-avatar">
                    {getInitials(employee.name)}
                  </div>
                );
              })()
            )}
          </div>
        </div>
      </header>

      {/* Main Layout Container */}
      <div className="ed-main-container">
        {/* Sidebar Navigation */}
        <aside className={`ed-sidebar-nav ${isSidebarOpen ? "ed-sidebar-open" : "ed-sidebar-closed"}`}>
          {/* Profile Section - Updated Version */}
          <ProfileSection
            employee={employee}
            formStatus={{
              personalInfo: formSubmitted,
              academic: true,
              experience: true,
              passport: true,
            }}
          />

          {/* Navigation Menu */}
          <div className="ed-nav-menu-container">
            {/* Personal Section */}
            <div className="ed-nav-section">
              <h5 className="ed-nav-section-title">PERSONAL</h5>
              <nav className="ed-nav-links">
                <button
                  className={`ed-nav-link ${activeTab === "Personal Details" ? 'ed-nav-active' : ''}`}
                  onClick={() => setActiveTab("Personal Details")}
                >
                  <span className="ed-nav-icon">
                    <MdPerson size={20} />
                  </span>
                  <span className="ed-nav-label">Profile</span>
                  {hasPersonalInfoData && <span className="ed-nav-badge ed-badge-complete"><MdCheckCircle size={14} /></span>}
                </button>
                
                <button
                  className={`ed-nav-link ${activeTab === "Academic Details" ? 'ed-nav-active' : ''}`}
                  onClick={() => setActiveTab("Academic Details")}
                >
                  <span className="ed-nav-icon">
                    <MdSchool size={20} />
                  </span>
                  <span className="ed-nav-label">Education</span>
                </button>
                
                <button
                  className={`ed-nav-link ${activeTab === "Experience Details" ? 'ed-nav-active' : ''}`}
                  onClick={() => setActiveTab("Experience Details")}
                >
                  <span className="ed-nav-icon">
                    <MdWork size={20} />
                  </span>
                  <span className="ed-nav-label">Experience</span>
                </button>
                
                <button
                  className={`ed-nav-link ${activeTab === "Document Records" ? 'ed-nav-active' : ''}`}
                  onClick={() => setActiveTab("Document Records")}
                >
                  <span className="ed-nav-icon">
                    <MdDescription size={20} />
                  </span>
                  <span className="ed-nav-label">Documents</span>
                </button>
              </nav>
            </div>

            {/* Work Section */}
            <div className="ed-nav-section">
              <h5 className="ed-nav-section-title">WORK</h5>
              <nav className="ed-nav-links">
                <button
                  className={`ed-nav-link ${activeTab === "Leave Management" ? 'ed-nav-active' : ''}`}
                  onClick={() => setActiveTab("Leave Management")}
                >
                  <span className="ed-nav-icon">
                    <MdEventNote size={20} />
                  </span>
                  <span className="ed-nav-label">Leave</span>
                </button>
                
                <button
                  className={`ed-nav-link ${activeTab === "Attendance Management" ? 'ed-nav-active' : ''}`}
                  onClick={() => setActiveTab("Attendance Management")}
                >
                  <span className="ed-nav-icon">
                    <MdLocationOn size={20} />
                  </span>
                  <span className="ed-nav-label">Attendance</span>
                </button>
                
                <button
                  className={`ed-nav-link ${activeTab === "Timesheet" ? 'ed-nav-active' : ''}`}
                  onClick={() => setActiveTab("Timesheet")}
                >
                  <span className="ed-nav-icon">
                    <MdAccessTime size={20} />
                  </span>
                  <span className="ed-nav-label">Timesheet</span>
                </button>
                
                <button
                  className={`ed-nav-link ${activeTab === "Employee Performance" ? 'ed-nav-active' : ''}`}
                  onClick={() => setActiveTab("Employee Performance")}
                >
                  <span className="ed-nav-icon">
                    <MdAssessment size={20} />
                  </span>
                  <span className="ed-nav-label">Performance</span>
                </button>
              </nav>
            </div>

            {/* Team Section */}
            <div className="ed-nav-section">
              <h5 className="ed-nav-section-title">TEAM</h5>
              <nav className="ed-nav-links">
                <button
                  className={`ed-nav-link ${activeTab === "My Team" ? 'ed-nav-active' : ''}`}
                  onClick={() => setActiveTab("My Team")}
                >
                  <span className="ed-nav-icon">
                    <MdGroup size={20} />
                  </span>
                  <span className="ed-nav-label">My Team</span>
                  {myTeam.length > 0 && <span className="ed-nav-count">{myTeam.length}</span>}
                </button>
                
                <button
                  className={`ed-nav-link ${activeTab === "Reporting Manager" ? 'ed-nav-active' : ''}`}
                  onClick={() => setActiveTab("Reporting Manager")}
                >
                  <span className="ed-nav-icon">
                    <MdSupervisorAccount size={20} />
                  </span>
                  <span className="ed-nav-label">Manager</span>
                </button>
                
                <button
                  className={`ed-nav-link ${activeTab === "Projects" ? 'ed-nav-active' : ''}`}
                  onClick={() => setActiveTab("Projects")}
                >
                  <span className="ed-nav-icon">
                    <MdFolder size={20} />
                  </span>
                  <span className="ed-nav-label">Projects</span>
                </button>
                
                <button
                  className={`ed-nav-link ${activeTab === "Chats" ? 'ed-nav-active' : ''}`}
                  onClick={() => setActiveTab("Chats")}
                >
                  <span className="ed-nav-icon">
                    <MdChat size={20} />
                  </span>
                  <span className="ed-nav-label">Chats</span>
                  <span className="ed-nav-badge">New</span>
                </button>
              </nav>
            </div>

            {/* Resources Section */}
            <div className="ed-nav-section">
              <h5 className="ed-nav-section-title">RESOURCES</h5>
              <nav className="ed-nav-links">
                <button
                  className={`ed-nav-link ${activeTab === "Tools" ? 'ed-nav-active' : ''}`}
                  onClick={() => setActiveTab("Tools")}
                >
                  <span className="ed-nav-icon">
                    <MdBuild size={20} />
                  </span>
                  <span className="ed-nav-label">Tools</span>
                </button>
                
                <button
                  className={`ed-nav-link ${activeTab === "Allocated Items" ? 'ed-nav-active' : ''}`}
                  onClick={() => setActiveTab("Allocated Items")}
                >
                  <span className="ed-nav-icon">
                    <MdInventory size={20} />
                  </span>
                  <span className="ed-nav-label">Allocated Items</span>
                </button>
                
                <button
                  className={`ed-nav-link ${activeTab === "Requirements" ? 'ed-nav-active' : ''}`}
                  onClick={() => setActiveTab("Requirements")}
                >
                  <span className="ed-nav-icon">
                    <MdAssignment size={20} />
                  </span>
                  <span className="ed-nav-label">Requirements</span>
                </button>
              </nav>
            </div>

            {/* Personal Details Button */}
            {!isHRView && !formSubmitted && (
              <div className="ed-sidebar-personal-info">
                <button 
                  className="ed-personal-info-btn"
                  onClick={() => {
                    const personalDetailsBtn = document.querySelector('.personal-details-container .info-btn');
                    if (personalDetailsBtn) personalDetailsBtn.click();
                    setActiveTab("Personal Details");
                  }}
                >
                  <span className="ed-btn-icon">
                    <MdEdit size={18} />
                  </span>
                  Update Personal Info
                </button>
              </div>
            )}

            {/* Logout Section */}
            <div className="ed-sidebar-footer">
              {isHRView ? (
                <button className="ed-logout-btn" onClick={handleBackToHR}>
                  <span className="ed-logout-icon">
                    <MdArrowBack size={18} />
                  </span>
                  Back to HR Dashboard
                </button>
              ) : (
                <button className="ed-logout-btn" onClick={handleLogout}>
                  <span className="ed-logout-icon">
                    <MdExitToApp size={18} />
                  </span>
                  Logout
                </button>
              )}
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="ed-main-content-area">
          {/* Tab Header */}
          <div className="ed-tab-header">
            <div className="ed-tab-title-wrapper">
              <p className="ed-tab-description">
                {activeTab === "Personal Details" && "View and manage your personal profile information"}
                {activeTab === "Academic Details" && "Manage your educational qualifications"}
                {activeTab === "Experience Details" && "View and update your work experience"}
                {activeTab === "Document Records" && "Access and manage your documents"}
                {activeTab === "Passport Details" && "Passport and travel document information"}
                {activeTab === "Leave Management" && "Apply for leave and view leave balance"}
                {activeTab === "Attendance Management" && "Track your attendance and working hours"}
                {activeTab === "Timesheet" && "Log your daily work hours and tasks"}
                {activeTab === "My Team" && `Team members at ${employee.company_name}`}
                {activeTab === "Reporting Manager" && "Contact and information about your reporting manager"}
                {activeTab === "Projects" && "Assigned projects and their status"}
                {activeTab === "Chats" && "Communicate with your team members"}
                {activeTab === "Tools" && "Company tools and resources available to you"}
                {activeTab === "Allocated Items" && "Company assets allocated to you"}
                {activeTab === "Employee Performance" && "Performance reviews and metrics"}
                {activeTab === "Requirements" && "Hiring requirements and job openings"}
              </p>
            </div>
            
            <div className="ed-tab-actions">
              {activeTab === "Personal Details" && !isHRView && (
                <div className="ed-action-buttons">
                  <button 
                    className="ed-action-btn ed-primary-btn"
                    onClick={() => {
                      const personalDetailsBtn = document.querySelector('.personal-details-container .info-btn');
                      if (personalDetailsBtn) personalDetailsBtn.click();
                    }}
                  >
                    <span className="ed-btn-icon"><MdEdit size={18} /></span>
                    {hasPersonalInfoData ? "Edit Profile" : "Add Personal Info"}
                  </button>
                  
                  <button 
                    className="ed-action-btn ed-secondary-btn"
                    onClick={refreshPersonalInfo}
                    disabled={loadingPersonalInfo}
                  >
                    <span className="ed-btn-icon"><MdRefresh size={18} /></span>
                    Refresh
                  </button>
                </div>
              )}
              
              {activeTab === "Reporting Manager" && (
                <button 
                  className="ed-action-btn ed-secondary-btn"
                  onClick={() => {
                    setReportingManager(null);
                    fetchReportingManager();
                  }}
                  disabled={managerLoading}
                >
                  <span className="ed-btn-icon"><MdRefresh size={18} /></span>
                  {managerLoading ? 'Loading...' : 'Refresh Manager'}
                </button>
              )}
              
              {activeTab === "My Team" && myTeam.length > 0 && (
                <button 
                  className="ed-action-btn ed-primary-btn"
                  onClick={() => setActiveTab("Chats")}
                >
                  <span className="ed-btn-icon"><MdChat size={18} /></span>
                  Chat with Team
                </button>
              )}
              
              {activeTab === "Chats" && (
                <button 
                  className="ed-action-btn ed-secondary-btn"
                  onClick={() => {}}
                >
                  <span className="ed-btn-icon"><MdRefresh size={18} /></span>
                  Refresh Chats
                </button>
              )}
            </div>
          </div>

          {/* Tab Content Container */}
          <div className="ed-tab-content-container">
            {/* Personal Details Tab */}
            {activeTab === "Personal Details" && (
              <div className="ed-tab-content ed-animate-fadeIn">
                <div className="ed-profile-dashboard">
                  {/* Quick Stats */}
                  <div className="ed-profile-stats-grid">
                    <div className="ed-stat-card">
                      <div className="ed-stat-icon-wrapper">
                        <span className="ed-stat-icon">
                          <MdPerson size={24} />
                        </span>
                      </div>
                      <div className="ed-stat-content">
                        <h3>Personal Info</h3>
                        <p className={`ed-stat-status ${hasPersonalInfoData ? 'ed-status-complete' : 'ed-status-incomplete'}`}>
                          {hasPersonalInfoData ? <><MdCheckCircle /> Complete</> : <><MdWarning /> Incomplete</>}
                        </p>
                      </div>
                    </div>
                    
                    <div className="ed-stat-card">
                      <div className="ed-stat-icon-wrapper">
                        <span className="ed-stat-icon">
                          <MdApartment size={24} />
                        </span>
                      </div>
                      <div className="ed-stat-content">
                        <h3>Department</h3>
                        <p className="ed-stat-value">{employee.department || "Not assigned"}</p>
                      </div>
                    </div>
                    
                    <div className="ed-stat-card">
                      <div className="ed-stat-icon-wrapper">
                        <span className="ed-stat-icon">
                          <MdCalendarToday size={24} />
                        </span>
                      </div>
                      <div className="ed-stat-content">
                        <h3>Employee Since</h3>
                        <p className="ed-stat-value">
                          {employee.joiningDate ? new Date(employee.joiningDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          }) : "Not specified"}
                        </p>
                      </div>
                    </div>
                    
                    <div className="ed-stat-card">
                      <div className="ed-stat-icon-wrapper">
                        <span className="ed-stat-icon">
                          <MdBusinessCenter size={24} />
                        </span>
                      </div>
                      <div className="ed-stat-content">
                        <h3>Role</h3>
                        <p className="ed-stat-value">{employee.role}</p>
                      </div>
                    </div>
                  </div>

                  {/* Main Content Grid */}
                  <div className="ed-profile-content-grid">
                    {/* Left Column - Personal Information */}
                    <div className="ed-profile-left-column">
                      {/* Personal Information Card */}
                      <div className="ed-info-card">
                        <div className="ed-card-header">
                          <h3 className="ed-card-title">
                            <span className="ed-card-title-icon">
                              <MdPerson size={20} />
                            </span>
                            Personal Information
                          </h3>
                          {hasPersonalInfoData ? (
                            <span className="ed-card-badge ed-badge-success">Complete</span>
                          ) : (
                            <span className="ed-card-badge ed-badge-warning">Incomplete</span>
                          )}
                        </div>
                        
                        <div className="ed-card-body">
                          {loadingPersonalInfo ? (
                            <div className="ed-loading-state">
                              <div className="ed-loading-spinner"></div>
                              <p>Loading personal information...</p>
                            </div>
                          ) : hasPersonalInfoData ? (
                            <div className="ed-personal-info-grid">
                              <div className="ed-info-item">
                                <span className="ed-info-label">Full Name</span>
                                <span className="ed-info-value">{employee.name}</span>
                              </div>
                              
                              <div className="ed-info-item">
                                <span className="ed-info-label">Employee ID</span>
                                <span className="ed-info-value ed-highlight-value">{employee.emp_id}</span>
                              </div>
                              
                              <div className="ed-info-item">
                                <span className="ed-info-label">Date of Birth</span>
                                <span className="ed-info-value">
                                  {personalInfo.dateOfBirth && isValidDate(personalInfo.dateOfBirth)
                                    ? new Date(personalInfo.dateOfBirth).toLocaleDateString('en-GB', {
                                        day: '2-digit',
                                        month: 'short',
                                        year: 'numeric'
                                      })
                                    : "Not specified"}
                                </span>
                              </div>
                              
                              <div className="ed-info-item">
                                <span className="ed-info-label">Blood Group</span>
                                <span className="ed-info-value ed-blood-group">
                                  {personalInfo.bloodGroup || "Not specified"}
                                </span>
                              </div>
                              
                              <div className="ed-info-item">
                                <span className="ed-info-label">Gender</span>
                                <span className="ed-info-value">{personalInfo.gender || "Not specified"}</span>
                              </div>
                              
                              <div className="ed-info-item">
                                <span className="ed-info-label">Nationality</span>
                                <span className="ed-info-value">{personalInfo.nationality || "Not specified"}</span>
                              </div>
                              
                              <div className="ed-info-item ed-full-width">
                                <span className="ed-info-label">Address</span>
                                <span className="ed-info-value">
                                  {personalInfo.address ? (
                                    <div className="ed-address-display">
                                      <span className="ed-address-icon">
                                        <MdLocationOn size={18} />
                                      </span>
                                      {personalInfo.address}
                                    </div>
                                  ) : "Not provided"}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="ed-empty-state">
                              <div className="ed-empty-icon"><MdEdit size={32} /></div>
                              <h4>No Personal Information</h4>
                              <p>Please add your personal details to complete your profile</p>
                              {!isHRView && (
                                <button 
                                  className="ed-action-btn ed-primary-btn"
                                  onClick={() => {
                                    const personalDetailsBtn = document.querySelector('.personal-details-container .info-btn');
                                    if (personalDetailsBtn) personalDetailsBtn.click();
                                  }}
                                >
                                  <span className="ed-btn-icon">+</span>
                                  Add Information
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {!isHRView && hasPersonalInfoData && (
                          <div className="ed-card-footer">
                            <button 
                              className="ed-action-btn ed-secondary-btn"
                              onClick={() => {
                                const personalDetailsBtn = document.querySelector('.personal-details-container .info-btn');
                                if (personalDetailsBtn) personalDetailsBtn.click();
                              }}
                            >
                              <span className="ed-btn-icon"><MdEdit size={18} /></span>
                              Edit Information
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Contact Information Card */}
                      <div className="ed-contact-info-card">
                        <div className="ed-contact-info-header">
                          <h3 className="ed-contact-info-title">
                            <span className="ed-contact-info-icon"><MdPhone size={20} /></span>
                            Contact Information
                          </h3>
                        </div>
                        
                        <div className="ed-contact-info-body">
                          <div className="ed-contact-info-list">
                            <div className="ed-contact-info-item">
                              <div className="ed-contact-icon-container">
                                <span className="ed-contact-icon-symbol"><MdEmail size={20} /></span>
                              </div>
                              <div className="ed-contact-details-wrapper">
                                <span className="ed-contact-detail-label">Personal Email</span>
                                <a href={`mailto:${employee.email}`} className="ed-contact-detail-value ed-contact-email-link">
                                  {employee.email}
                                </a>
                              </div>
                            </div>
                            
                            <div className="ed-contact-info-item">
                              <div className="ed-contact-icon-container">
                                <span className="ed-contact-icon-symbol"><MdBusiness size={20} /></span>
                              </div>
                              <div className="ed-contact-details-wrapper">
                                <span className="ed-contact-detail-label">Company Email</span>
                                <a href={`mailto:${employee.company_email}`} className="ed-contact-detail-value ed-contact-email-link">
                                  {employee.company_email}
                                </a>
                              </div>
                            </div>
                            
                            <div className="ed-contact-info-item">
                              <div className="ed-contact-icon-container">
                                <span className="ed-contact-icon-symbol"><MdPhone size={20} /></span>
                              </div>
                              <div className="ed-contact-details-wrapper">
                                <span className="ed-contact-detail-label">Mobile Number</span>
                                <span className="ed-contact-detail-value">
                                  {personalInfo?.mobileNumber ? (
                                    <a href={`tel:${personalInfo.mobileNumber}`} className="ed-contact-phone-link">
                                      {formatPhoneNumber(personalInfo.mobileNumber)}
                                    </a>
                                  ) : <span className="ed-contact-no-data">Not provided</span>}
                                </span>
                              </div>
                            </div>
                            
                            <div className="ed-contact-info-item">
                              <div className="ed-contact-icon-container">
                                <span className="ed-contact-icon-symbol"><MdWarning size={20} /></span>
                              </div>
                              <div className="ed-contact-details-wrapper">
                                <span className="ed-contact-detail-label">Emergency Contact</span>
                                <span className="ed-contact-detail-value">
                                  {personalInfo?.emergencyContact ? (
                                    <a href={`tel:${personalInfo.emergencyContact}`} className="ed-contact-phone-link">
                                      {formatPhoneNumber(personalInfo.emergencyContact)}
                                    </a>
                                  ) : <span className="ed-contact-no-data">Not provided</span>}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column - Professional & Company Info */}
                    <div className="ed-profile-right-column">
                      {/* Professional Details Card */}
                      <div className="ed-info-card">
                        <div className="ed-card-header">
                          <h3 className="ed-card-title">
                            <span className="ed-card-title-icon"><MdWork size={20} /></span>
                            Professional Details
                          </h3>
                        </div>
                        
                        <div className="ed-card-body">
                          <div className="ed-professional-grid">
                            <div className="ed-professional-item">
                              <span className="ed-professional-label">Designation</span>
                              <span className="ed-professional-value">{employee.role}</span>
                            </div>
                            
                            <div className="ed-professional-item">
                              <span className="ed-professional-label">Department</span>
                              <span className="ed-professional-value">{employee.department}</span>
                            </div>
                            
                            <div className="ed-professional-item">
                              <span className="ed-professional-label">Reporting Manager</span>
                              <span className="ed-professional-value">{employee.reporting_manager || "Not assigned"}</span>
                            </div>
                            
                            <div className="ed-professional-item">
                              <span className="ed-professional-label">Allocated Items</span>
                              <span className="ed-professional-value">{employee.allocated_item || "None"}</span>
                            </div>
                            
                            <div className="ed-professional-item">
                              <span className="ed-professional-label">Join Date</span>
                              <span className="ed-professional-value">Not specified</span>
                            </div>
                            
                            <div className="ed-professional-item">
                              <span className="ed-professional-label">Experience</span>
                              <span className="ed-professional-value">Not specified</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Company Information Card */}
                      <div className="ed-info-card">
                        <div className="ed-card-header">
                          <h3 className="ed-card-title">
                            <span className="ed-card-title-icon"><MdBusiness size={20} /></span>
                            Company Information
                          </h3>
                        </div>
                        
                        <div className="ed-card-body">
                          <div className="ed-company-display">
                            <div className="ed-company-logo-section">
                              <div className="ed-company-logo">
                                {employee.company_name?.charAt(0)}
                              </div>
                              <div className="ed-company-info">
                                <h4 className="ed-company-name">{employee.company_name}</h4>
                                <span className="ed-company-badge">Official Partner</span>
                              </div>
                            </div>
                            
                            <div className="ed-company-metrics">
                              <div className="ed-company-metric">
                                <span className="ed-metric-value">1</span>
                                <span className="ed-metric-label">Department</span>
                              </div>
                              <div className="ed-company-metric">
                                <span className="ed-metric-value">Active</span>
                                <span className="ed-metric-label">Status</span>
                              </div>
                              <div className="ed-company-metric">
                                <span className="ed-metric-value">Full-time</span>
                                <span className="ed-metric-label">Employment</span>
                              </div>
                              <div className="ed-company-metric">
                                <span className="ed-metric-value">{employee.emp_id}</span>
                                <span className="ed-metric-label">Employee ID</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Quick Actions Card */}
                      <div className="ed-info-card">
                        <div className="ed-card-header">
                          <h3 className="ed-card-title">
                            <span className="ed-card-title-icon"><MdMoreVert size={20} /></span>
                            Quick Actions
                          </h3>
                        </div>
                        
                        <div className="ed-card-body">
                          <div className="ed-quick-actions">
                            <button 
                              className="ed-quick-action-btn"
                              onClick={() => {
                                if (employee.reporting_manager) {
                                  setActiveTab("Reporting Manager");
                                }
                              }}
                            >
                              <span className="ed-action-icon"><MdSupervisorAccount size={18} /></span>
                              Contact Manager
                            </button>
                            
                            <button 
                              className="ed-quick-action-btn"
                              onClick={() => setActiveTab("My Team")}
                            >
                              <span className="ed-action-icon"><MdGroup size={18} /></span>
                              View Team
                            </button>
                            
                            <button 
                              className="ed-quick-action-btn"
                              onClick={() => setActiveTab("Allocated Items")}
                            >
                              <span className="ed-action-icon"><MdInventory size={18} /></span>
                              View Items
                            </button>
                            
                            <button 
                              className="ed-quick-action-btn"
                              onClick={() => setActiveTab("Document Records")}
                            >
                              <span className="ed-action-icon"><MdDescription size={18} /></span>
                              View Documents
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Academic Details Tab */}
            {activeTab === "Academic Details" && (
              <div className="ed-tab-content ed-animate-fadeIn">
                <AcademicDetails employee={employee} />
              </div>
            )}

            {/* Experience Details Tab */}
            {activeTab === "Experience Details" && (
              <div className="ed-tab-content ed-animate-fadeIn">
                <ExperienceDetails employee={employee} />
              </div>
            )}

            {/* Document Records Tab */}
            {activeTab === "Document Records" && (
              <div className="ed-tab-content ed-animate-fadeIn">
                <DocumentRecords employee={employee} />
              </div>
            )}

            {/* My Team Tab */}
            {activeTab === "My Team" && (
              <div className="ed-tab-content ed-animate-fadeIn">
                <div className="ed-team-header">
                  <h2 className="ed-team-title">
                    My Team - {employee.company_name}
                    <span className="ed-team-count">({myTeam.length} members)</span>
                  </h2>
                  <div className="ed-team-actions">
                    <button 
                      className="ed-action-btn ed-secondary-btn"
                      onClick={() => {}}
                    >
                      <span className="ed-btn-icon"><MdRefresh size={18} /></span>
                      Refresh
                    </button>
                    <button 
                      className="ed-action-btn ed-primary-btn"
                      onClick={() => setActiveTab("Chats")}
                    >
                      <span className="ed-btn-icon"><MdChat size={18} /></span>
                      Team Chat
                    </button>
                  </div>
                </div>
                
                {myTeam.length > 0 ? (
                  <div className="ed-team-grid">
                    {myTeam.map((member, index) => {
                      const memberProfilePic = member.profilePicture 
                        ? `${API_BASE_URL}${member.profilePicture}`
                        : member.profilePic 
                        ? `${API_BASE_URL}${member.profilePic}`
                        : null;
                      
                      return (
                        <div 
                          key={member._id} 
                          className="ed-team-card ed-animate-slideIn" 
                          style={{ animationDelay: `${index * 0.1}s` }}
                        >
                          <div className="ed-team-card-header">
                            <div className="ed-team-avatar-container">
                              {memberProfilePic ? (
                                <img 
                                  src={memberProfilePic} 
                                  alt={`${member.name}'s Profile`} 
                                  className="ed-team-profile-pic"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div 
                                className="ed-team-avatar" 
                                style={{ display: memberProfilePic ? 'none' : 'flex' }}
                              >
                                {member.name ? member.name.split(" ").map((n) => n[0]).join("").toUpperCase() : '?'}
                              </div>
                            </div>
                            
                            <div className="ed-team-basic-info">
                              <h4 className="ed-team-member-name">{member.name || 'Unknown'}</h4>
                              <p className="ed-team-member-role">{member.role || 'No role specified'}</p>
                              <span className="ed-team-member-id">ID: {member.emp_id || 'N/A'}</span>
                            </div>
                          </div>
                          
                          <div className="ed-team-card-body">
                            <div className="ed-team-details">
                              <div className="ed-team-detail">
                                <span className="ed-detail-label">Email</span>
                                <span className="ed-detail-value">
                                  {member.company_email || member.email || 'No email'}
                                </span>
                              </div>
                              
                              <div className="ed-team-detail">
                                <span className="ed-detail-label">Department</span>
                                <span className="ed-detail-value">{member.department || 'No department'}</span>
                              </div>
                              
                              {member.reporting_manager && (
                                <div className="ed-team-detail">
                                  <span className="ed-detail-label">Manager</span>
                                  <span className="ed-detail-value">{member.reporting_manager}</span>
                                </div>
                              )}
                              
                              {member.allocated_item && (
                                <div className="ed-team-detail">
                                  <span className="ed-detail-label">Allocated Items</span>
                                  <span className="ed-detail-value">{member.allocated_item}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="ed-team-card-footer">
                            <button 
                              className="ed-team-contact-btn"
                              onClick={() => handleContactClick(member)}
                              disabled={!member._id}
                            >
                              <span className="ed-btn-icon"><MdChat size={16} /></span>
                              Chat
                            </button>
                            
                            <button 
                              className="ed-team-view-btn"
                              onClick={() => {}}
                            >
                              <span className="ed-btn-icon"><MdPerson size={16} /></span>
                              View
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="ed-no-data">
                    <div className="ed-empty-state">
                      <div className="ed-empty-icon"><MdGroup size={32} /></div>
                      <h4>No Team Members Found</h4>
                      <p>There are no other employees in your company yet.</p>
                      <button 
                        className="ed-action-btn ed-secondary-btn"
                        onClick={() => {}}
                      >
                        <span className="ed-btn-icon"><MdRefresh size={18} /></span>
                        Refresh List
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reporting Manager Tab */}
            {activeTab === "Reporting Manager" && (
              <div className="rm-tab-panel rm-animate-fadeIn">
                <div className="rm-panel-header">
                  <h2 className="rm-panel-title">Reporting Manager</h2>
                  <p className="rm-panel-subtitle">View your reporting manager's details</p>
                  <button 
                    className="rm-refresh-btn"
                    onClick={() => {
                      setReportingManager(null);
                      fetchReportingManager();
                    }}
                    disabled={managerLoading}
                  >
                    {managerLoading ? <><MdRefresh /> Loading...</> : <><MdRefresh /> Refresh</>}
                  </button>
                </div>
                
                {managerLoading ? (
                  <div className="rm-loading-state">
                    <div className="rm-loading-spinner"></div>
                    <p>Loading manager details...</p>
                  </div>
                ) : reportingManager ? (
                  <div className="rm-manager-container">
                    <div className="rm-manager-card rm-animate-slideIn">
                      
                      {/* Profile Section */}
                      <div className="rm-manager-profile-section">
                        <div className="rm-manager-avatar-container">
                          {(() => {
                            const profilePic = reportingManager.profileImageUrl || 
                                              reportingManager.profileImage || 
                                              getProfilePicture(reportingManager);
                            
                            if (profilePic) {
                              return (
                                <div className="rm-manager-pic-wrapper">
                                  <img 
                                    src={profilePic} 
                                    alt={`${reportingManager.name}'s profile`}
                                    className="rm-manager-profile-pic"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      const avatarFallback = e.target.nextElementSibling;
                                      if (avatarFallback) avatarFallback.style.display = 'flex';
                                    }}
                                  />
                                  <div 
                                    className="rm-manager-avatar rm-avatar-fallback"
                                    style={{ display: 'none' }}
                                  >
                                    {getInitials(reportingManager.name)}
                                  </div>
                                </div>
                              );
                            } else {
                              return (
                                <div className="rm-manager-avatar">
                                  {getInitials(reportingManager.name)}
                                </div>
                              );
                            }
                          })()}
                          
                          <div className="rm-manager-badge">
                            <span className="rm-badge-icon"><MdSupervisorAccount size={18} /></span>
                            <span className="rm-badge-text">
                              Reporting Manager
                            </span>
                          </div>
                        </div>
                        
                        <div className="rm-manager-basic-info">
                          <h3 className="rm-manager-name">
                            {reportingManager.name || "Reporting Manager"}
                          </h3>
                          
                          <div className="rm-manager-role-dept">
                            <span className="rm-manager-role">
                              <span className="rm-role-icon"><MdWork size={16} /></span>
                              Reporting Manager
                            </span>
                            <span className="rm-manager-dept">
                              <span className="rm-dept-icon"><MdBusiness size={16} /></span>
                              {reportingManager.department || "Not specified"}
                            </span>
                          </div>
                          
                          <div className="rm-manager-ids">
                            <span className="rm-manager-id">
                              <span className="rm-id-icon"><MdAssignment size={16} /></span>
                              Manager ID: {reportingManager.manager_id || "N/A"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Contact Information */}
                      <div className="rm-manager-contact-section">
                        <h4 className="rm-section-title">Contact Information</h4>
                        
                        <div className="rm-contact-grid">
                          <div className="rm-contact-item">
                            <div className="rm-contact-icon"><MdEmail size={18} /></div>
                            <div className="rm-contact-details">
                              <div className="rm-contact-label">Company Email</div>
                              <div className="rm-contact-value">
                                {reportingManager.email ? (
                                  <a 
                                    href={`mailto:${reportingManager.email}`} 
                                    className="rm-email-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {reportingManager.email}
                                  </a>
                                ) : (
                                  <span className="rm-no-data">Not available</span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="rm-contact-item">
                            <div className="rm-contact-icon"><MdBusiness size={18} /></div>
                            <div className="rm-contact-details">
                              <div className="rm-contact-label">Department</div>
                              <div className="rm-contact-value">
                                {reportingManager.department || "Not specified"}
                              </div>
                            </div>
                          </div>
                          
                          <div className="rm-contact-item">
                            <div className="rm-contact-icon"><MdApartment size={18} /></div>
                            <div className="rm-contact-details">
                              <div className="rm-contact-label">Company</div>
                              <div className="rm-contact-value">
                                {reportingManager.company_name || employee.company_name || "Not specified"}
                              </div>
                            </div>
                          </div>
                          
                          {reportingManager.location && (
                            <div className="rm-contact-item">
                              <div className="rm-contact-icon"><MdLocationOn size={18} /></div>
                              <div className="rm-contact-details">
                                <div className="rm-contact-label">Location</div>
                                <div className="rm-contact-value">{reportingManager.location}</div>
                              </div>
                            </div>
                          )}
                          
                          {/* Status Indicator */}
                          <div className="rm-contact-item">
                            <div className="rm-contact-icon">●</div>
                            <div className="rm-contact-details">
                              <div className="rm-contact-label">Status</div>
                              <div className="rm-contact-value">
                                <span className={`rm-status-indicator ${reportingManager.isOnline ? 'rm-status-online' : 'rm-status-offline'}`}>
                                  {reportingManager.isOnline ? 'Online' : 'Offline'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : employee.reporting_manager ? (
                  <div className="rm-no-manager">
                    <div className="rm-placeholder-content">
                      <div className="rm-placeholder-icon"><MdSupervisorAccount size={32} /></div>
                      <h4>Manager Not Found</h4>
                      <p>Could not find details for: <strong>{employee.reporting_manager}</strong></p>
                      <p className="rm-help-text">
                        The manager might not be in the system or the name might be spelled differently.
                      </p>
                      <div className="rm-action-buttons">
                        <button 
                          className="rm-primary-btn"
                          onClick={() => {
                            setReportingManager(null);
                            fetchReportingManager();
                          }}
                        >
                          Try Again
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="rm-no-manager">
                    <div className="rm-placeholder-content">
                      <div className="rm-placeholder-icon"><MdSupervisorAccount size={32} /></div>
                      <h4>No Reporting Manager Assigned</h4>
                      <p>You don't have a reporting manager assigned yet.</p>
                      <p className="rm-help-text">
                        Please contact your HR department to assign a reporting manager.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {managerChatOpen && (
              <div className="manager-chat-modal-overlay">
                <div className="manager-chat-modal">
                  {/* Chat Header */}
                  <div className="manager-chat-header">
                    <div className="manager-chat-header-left">
                      <div className="manager-chat-avatar">
                        {selectedManager?.profileImage ? (
                          <img 
                            src={getProfilePicture(selectedManager)}
                            alt={selectedManager?.name}
                            className="manager-chat-avatar-img"
                          />
                        ) : (
                          <div className="manager-chat-avatar-initial">
                            {getInitials(selectedManager?.name)}
                          </div>
                        )}
                      </div>
                      <div className="manager-chat-info">
                        <h4 className="manager-chat-name">
                          {selectedManager?.name || 'Reporting Manager'}
                        </h4>
                        <p className="manager-chat-title">
                          {selectedManager?.department} Manager
                          <span className="manager-chat-status">
                            ● Online
                          </span>
                        </p>
                      </div>
                    </div>
                    
                    <button 
                      className="manager-chat-close"
                      onClick={() => {
                        setManagerChatOpen(false);
                        setSelectedManager(null);
                        setManagerMessages([]);
                      }}
                    >
                      <MdClose size={20} />
                    </button>
                  </div>
                  
                  {/* Messages Container */}
                  <div className="manager-chat-messages">
                    {managerMessages.length === 0 ? (
                      <div className="manager-chat-empty">
                        <div className="empty-chat-icon"><MdChat size={32} /></div>
                        <p>Start a conversation with your manager</p>
                        <small>This chat is only visible to you and your reporting manager</small>
                      </div>
                    ) : (
                      managerMessages.map((msg) => (
                        <div 
                          key={msg._id || msg.timestamp}
                          className={`manager-message ${
                            msg.senderId === employee._id ? 'manager-message-sent' : 'manager-message-received'
                          } ${msg.status === 'failed' ? 'manager-message-failed' : ''}`}
                        >
                          <div className="manager-message-content">
                            <p>{msg.message}</p>
                            <div className="manager-message-meta">
                              <span className="manager-message-time">
                                {new Date(msg.timestamp).toLocaleTimeString([], { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </span>
                              {msg.senderId === employee._id && (
                                <span className="manager-message-status">
                                  {msg.status === 'sending' ? '🔄' : 
                                  msg.status === 'sent' ? '✓' : 
                                  msg.status === 'failed' ? '✗' : '✓'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  
                  {/* Input Area */}
                  <div className="manager-chat-input-area">
                    <input
                      type="text"
                      placeholder="Type your message..."
                      className="manager-chat-input"
                      value={managerMessageInput}
                      onChange={(e) => setManagerMessageInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && managerMessageInput.trim()) {
                          sendManagerMessage();
                        }
                      }}
                    />
                    <button 
                      className="manager-chat-send"
                      onClick={sendManagerMessage}
                      disabled={!managerMessageInput.trim()}
                    >
                      <span className="send-icon">➤</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Allocated Items Tab */}
            {activeTab === "Allocated Items" && (
              <div className="ed-tab-content ed-animate-fadeIn">
                <div className="ed-items-header">
                  <h2 className="ed-items-title">My Allocated Items</h2>
                  <p className="ed-items-subtitle">Company assets and resources assigned to you</p>
                </div>
                
                {allocatedItems.length > 0 ? (
                  <div className="ed-items-grid">
                    {allocatedItems.map((item, index) => (
                      <div key={index} className="ed-item-card ed-animate-slideIn" style={{ animationDelay: `${index * 0.1}s` }}>
                        <div className="ed-item-icon">
                          <MdInventory size={24} />
                        </div>
                        <div className="ed-item-content">
                          <h4 className="ed-item-name">{typeof item === 'object' ? item.name : item}</h4>
                          <p className="ed-item-status ed-status-allocated">Allocated</p>
                          <p className="ed-item-date">Assigned on: {new Date().toLocaleDateString()}</p>
                          <div className="ed-item-details">
                            <span className="ed-item-detail">Serial: N/A</span>
                            <span className="ed-item-detail">Condition: Good</span>
                          </div>
                        </div>
                        <div className="ed-item-actions">
                          <button className="ed-item-action-btn ed-view-btn">View Details</button>
                          <button className="ed-item-action-btn ed-request-btn">Request Service</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ed-no-data">
                    <div className="ed-empty-state">
                      <div className="ed-empty-icon"><MdInventory size={32} /></div>
                      <h4>No Items Allocated</h4>
                      <p>No company assets have been allocated to you yet.</p>
                      <button 
                        className="ed-action-btn ed-secondary-btn"
                        onClick={() => {}}
                      >
                        <span className="ed-btn-icon"><MdAssignment size={18} /></span>
                        Request Items
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Leave Management Tab */}
            {activeTab === "Leave Management" && (
              <div className="ed-tab-content ed-animate-fadeIn">
                <LeaveManagement employee={employee} />
              </div>
            )}

            {/* Attendance Management Tab */}
            {activeTab === "Attendance Management" && (
              <div className="ed-tab-content ed-animate-fadeIn">
                <AttendanceManagement employee={employee} />
              </div>
            )}

            {/* Timesheet Tab */}
            {activeTab === "Timesheet" && (
              <div className="ed-tab-content ed-animate-fadeIn">
                <Timesheets employee={employee} />
              </div>
            )}

            {/* Projects Tab */}
            {activeTab === "Projects" && (
              <div className="ed-tab-content ed-animate-fadeIn">
                <EmployeeProjects employee={employee} />
              </div>
            )}

            {/* Tools Tab */}
            {activeTab === "Tools" && (
              <div className="ed-tab-content ed-animate-fadeIn">
                <EmployeeMeetings employee={employee} />
              </div>
            )}

            {/* Employee Performance Tab */}
            {activeTab === "Employee Performance" && (
              <div className="ed-tab-content ed-animate-fadeIn">
                <EmployeePerformance employee={employee} />
              </div>
            )}

            {/* Chats Tab */}
            {activeTab === "Chats" && (
              <div className="ed-tab-content ed-animate-fadeIn">
                <ChatsTab employee={employee} />
              </div>
            )}

            {/* Requirements Tab */}
            {activeTab === "Requirements" && (
              <div className="ed-tab-content ed-animate-fadeIn">
                <HiringRequirements employee={employee} isHRView={isHRView} />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modals */}
      <TeamChatModal
        isOpen={chatModalOpen}
        onClose={handleCloseChat}
        teamMember={selectedTeamMember}
        currentEmployee={employee}
      />
      
      {managerChatOpen && (
        <div className="manager-chat-modal-overlay">
          {/* Manager Chat Modal Content */}
        </div>
      )}
      {/* Personal Details Modal Component (if using separate component) */}
      <PersonalDetails
        employee={employee}
        formSubmitted={formSubmitted}
        setFormSubmitted={setFormSubmitted}
        onUpdate={handlePersonalInfoUpdate}
      />
    </div>
  );
};

export default EmployeeDashboard;